/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import Image from 'next/image'
import { ModalProvider, Button, Text } from '@hashicorp/mds-react'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'
import { vercelSplit } from '@web/cms'
import Anchor from 'components/materia/anchor'
import StructuredTextBlock from 'components/materia/sub-blocks/structured-text'
import { useEffect, useState } from 'react'
import { getModalType } from 'app/[locale]/abm/_utils/get-modal-type'
import { ABMModalContent } from 'app/[locale]/abm/_components/abm-tabs/modal-content'
import type { AbmLinkPreviewFieldsFragment } from 'types/dato'
import s from './abm-link-preview.module.css'

const scrollToHash = (hash) => {
	const el = document.getElementById(hash)
	if (el) {
		el.scrollIntoView({ behavior: 'smooth' })
	}
}

const ABMLinkPreview = (props: AbmLinkPreviewFieldsFragment) => {
	const [initialIsOpen, setInitialIsOpen] = useState(null)
	const parseHref = parseUrl(props.href).href

	useEffect(() => {
		const hash = window.location.hash.slice(1)
		const timeoutId = setTimeout(() => scrollToHash(hash), 100)

		if (hash && hash === vercelSplit(props.anchorId)) {
			setInitialIsOpen(true)
		} else {
			setInitialIsOpen(false)
		}

		return () => clearTimeout(timeoutId)
	}, [])

	if (initialIsOpen === null) return null

	return (
		<ModalProvider
			initialIsOpen={initialIsOpen}
			initialModalContent={<ABMLinkPreviewModal {...props} href={parseHref} />}
			onClose={() => {
				setTimeout(() => scrollToHash(props.anchorId), 100)
			}}
		>
			<Anchor anchorId={props.anchorId} />
		</ModalProvider>
	)
}

const ABMLinkPreviewModal = ({
	theme,
	href,
	image,
	title,
	description,
}: AbmLinkPreviewFieldsFragment) => {
	const modalType = getModalType(href)

	if (modalType === 'externalLink') {
		return (
			<div className={s.root} data-theme={vercelSplit(theme)}>
				<div className={s.header}>
					{title && (
						<Text.DisplayExpressive color="strong" size="300">
							{title}
						</Text.DisplayExpressive>
					)}
					<Button
						text="View full page"
						icon="external-link"
						iconPosition="trailing"
						color="secondary-high-contrast"
						href={href}
						isHrefExternal
					/>
				</div>
				{image && (
					<Image
						{...image}
						src={image.url}
						alt={image?.alt || title || 'Link preview image'}
						className={s.image}
					/>
				)}
				{description && <StructuredTextBlock {...description} />}
			</div>
		)
	}

	return <ABMModalContent href={href} />
}

export { ABMLinkPreview }
